import * as React from 'react';
import { getUnit } from '@/api/units';
import { Box, Button, Container, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { BuildingApartment as BuildingApartmentIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

import { UnitHeader } from './components/unit-header';
import { UnitSidebar } from './components/unit-sidebar';
import { UnitResidentsSection } from './components/unit-residents-section';
import { UnitRequestsSection } from './components/unit-requests-section';

interface UnitResponse {
  unit: Unit;
}

export function UnitPage(): React.JSX.Element {
  const { community } = useUser();
  const { unitId } = useParams<{ unitId: string }>();
  const theme = useTheme();

  if (!unitId) {
    return <div>Unit ID is required</div>;
  }

  const { data, isLoading, error, refetch } = useQuery<UnitResponse>({
    queryKey: [`unit-${unitId}`, community?.id],
    queryFn: async () => {
      const unit = await getUnit(unitId);
      return { unit };
    },
  });
  const unit = data?.unit;

  if (!isLoading && error?.message === 'Unit not found') {
    return (
      <>
        <Seo title="Unit Not Found" />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <BuildingApartmentIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Unit not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This unit doesn't exist or has been removed.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">Go Back</Button>
                <Button href={paths.units.index} variant="contained">All Units</Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title={unit?.address ? `${unit.address}${unit.unit_number ? ` — Unit ${unit.unit_number}` : ''}` : 'Unit'} />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <UnitHeader unit={unit} isLoading={isLoading} />
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            {/* ── Left column: Residents + Requests ── */}
            <Grid md={8} xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Residents */}
                <Box sx={{ p: 3 }}>
                  <UnitResidentsSection unitId={unitId} />
                </Box>

                {/* Requests */}
                <Divider />
                <Box sx={{ p: 3 }}>
                  <UnitRequestsSection unitId={unitId} communityId={community?.id || ''} />
                </Box>
              </Paper>
            </Grid>

            {/* ── Right column: Property details sidebar ── */}
            <Grid md={4} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: { md: 'sticky' },
                  top: { md: 24 },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <UnitSidebar unit={unit} isLoading={isLoading} onRefresh={refetch} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
